import React from 'react';
import { Box, Card, CardContent, Typography, List, ListItem, ListItemText, Chip, Stack, Link } from '@mui/material';
import { Disease } from '../types';

interface IndicationDetailsProps {
  disease: Disease | null;
}

export const IndicationDetails: React.FC<IndicationDetailsProps> = ({ disease }) => {
  if (!disease) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body1" color="text.secondary">
            Select an indication to view details
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {disease.scientificName || disease.name}
          </Typography>
          {disease.description && (
            <Typography variant="body1" paragraph>
              {disease.description}
            </Typography>
          )}
          {disease.geneOfInterest && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Gene(s) of Interest
              </Typography>
              <Typography variant="body2">
                {disease.geneOfInterest}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Key Statistics
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Patient Population"
                secondary={disease.initialPatientPopulation.toLocaleString()}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="New Patients per Year"
                secondary={disease.newPatientsPerYear.toLocaleString()}
              />
            </ListItem>
            {disease.economicCostPerPatient && (
              <ListItem>
                <ListItemText
                  primary="Economic Cost per Patient"
                  secondary={`$${disease.economicCostPerPatient.toLocaleString()}`}
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>

      {disease.companies && disease.companies.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Companies Involved
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {disease.companies.map((company) => (
                <Chip key={company} label={company} size="small" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {disease.citations && Object.keys(disease.citations).length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Sources
            </Typography>
            <List dense>
              {Object.entries(disease.citations).map(([key, urls]) =>
                urls.length > 0 ? (
                  <ListItem key={key}>
                    <ListItemText
                      primary={key.split('_').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                      secondary={urls.map((url, index) => (
                        <Link
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            display: 'block',
                            wordBreak: 'break-all',
                            mb: index < urls.length - 1 ? 0.5 : 0 
                          }}
                        >
                          {url}
                        </Link>
                      ))}
                    />
                  </ListItem>
                ) : null
              )}
            </List>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};
